// useAttachments — les captures jointes à un message.
//
// ── CE QU'IL EST, ET CE QU'IL N'EST PAS ─────────────────────────────────────
//
// Il ne décide RIEN. Les plafonds (1024 px, deux images), les types permis, le calcul du
// coût en jetons et les messages de refus vivent dans src/lib/attachment.ts, en fonctions
// pures, éprouvées par 21 tests et 19 mutations. Ici il n'y a que ce qu'on ne peut pas
// éprouver sans navigateur : lire un fichier, le redessiner plus petit, le ré-encoder.
//
// ── CE QUI A ÉTÉ RETIRÉ LE 2026-10-04, ET POURQUOI ──────────────────────────
//
// Une capture d'écran par `getDisplayMedia` a vécu ici une journée. Elle demandait une
// autorisation à chaque fois, n'existait sur AUCUN navigateur mobile, et ne pouvait isoler
// l'aperçu que si l'opérateur choisissait exactement « cet onglet » dans une fenêtre du
// navigateur. Deux essais, deux échecs chez lui.
//
// Son système, lui, sait déjà faire ça mieux : il choisit la zone exacte, sans autorisation,
// partout. La valeur était ailleurs — dans le DESSIN, qui reste. Garder la machinerie « au
// cas où » aurait laissé un chemin que presque personne n'emprunte et que personne ne
// corrige.
//
// ── POURQUOI ON RÉ-ENCODE TOUJOURS, MÊME UNE PETITE IMAGE ───────────────────
//
// C'est le levier de coût. Une capture d'écran PNG de 1024 px pèse facilement 1,5 Mo ; la
// même en WebP de qualité 0,8 en pèse 150 Ko. Laisser passer les octets d'origine, c'est
// envoyer dix fois plus sur le réseau à chaque tour du run — et la documentation du
// fournisseur est explicite : « each request resends the full conversation history ».
//
// ── ET POURQUOI ON VÉRIFIE CE QUE LE NAVIGATEUR A PRODUIT ───────────────────
//
// On demande du WebP. Ce que rend un navigateur qui ne le connaît pas n'est PAS documenté
// de façon que j'aie pu vérifier (la page de référence n'a pas répondu le 2026-10-03). Donc
// on lit le type réellement produit et on le juge (`dataUrlType` + `acceptedImageType`) :
// un contrôle à l'exécution vaut mieux qu'une hypothèse. Du PNG en retour est parfaitement
// acceptable — c'est l'un des quatre types que le fournisseur lit.
import { useCallback, useRef, useState } from "react";
import {
  ATTACH_MAX_IMAGES,
  acceptedImageType,
  attachRefusal,
  dataUrlType,
  fittedSize,
} from "../lib/attachment";
import { type Stroke } from "../lib/annotate";
import {
  DOC_MAX_FILES,
  documentBudgetNote,
  documentKind,
  documentRefusal,
} from "../lib/document";
import { readDocumentText } from "../lib/document_read";
import { paintStrokes } from "../lib/paint_strokes";

/** La qualité du ré-encodage. 0,8 garde un texte d'interface lisible — le fournisseur
 *  prévient qu'« heavy JPEG compression can make text difficult to read » — tout en
 *  divisant le poids par dix par rapport au PNG d'origine. */
const QUALITE = 0.8;

export type Attachment = {
  /** L'image telle qu'elle PART : l'originale, avec les traits dessinés dessus s'il y en a. */
  readonly url: string;
  /** L'originale, SANS aucun trait. Gardée exprès : l'image annotée est REDESSINÉE depuis
   *  elle à chaque changement, donc corriger dix fois ne dégrade rien. Repartir de la
   *  dernière image annotée ré-encoderait du déjà ré-encodé, et le texte de l'app — ce
   *  qu'on cherche justement à montrer — finirait illisible. */
  readonly original: string;
  /** Les traits, en DONNÉES, dans le repère de l'image. Annuler retire un trait ; il n'y a
   *  jamais de pixels à « effacer ». */
  readonly strokes: readonly Stroke[];
  readonly width: number;
  readonly height: number;
};

/** UN DOCUMENT JOINT, déjà réduit à son TEXTE par le navigateur.
 *
 *  Pas de fichier, pas d'octets : du texte. C'est la décision qui rend la fonction gratuite
 *  côté modèle — voir l'en-tête de src/lib/document.ts. */
export type AttachedDocument = {
  readonly name: string;
  readonly text: string;
};

export type Attachments = {
  readonly items: readonly Attachment[];
  /** Les DOCUMENTS joints (pdf, docx, txt…), réduits à leur texte. */
  readonly documents: readonly AttachedDocument[];
  /** Retirer un document. Séparé de `remove` : les deux listes sont indépendantes, et un
   *  index partagé aurait fini par retirer la mauvaise pièce jointe. */
  readonly removeDocument: (index: number) => void;
  /** Pourquoi la dernière tentative n'a rien ajouté. */
  readonly note: string | null;
  readonly add: (files: ArrayLike<File>) => Promise<void>;
  /** Une capture que la PLATEFORME a prise de l'app (le bouton « capturer »). Elle passe
   *  par la MÊME réduction qu'un fichier choisi à la main : même plafond, même coût. */
  readonly addDataUrl: (url: string) => Promise<void>;
  /** Remplacer les traits d'une pièce jointe. L'image qui PART est redessinée depuis
   *  l'originale — jamais depuis la précédente version annotée. */
  readonly setStrokes: (index: number, strokes: readonly Stroke[]) => Promise<void>;
  /** Poser une raison venue d'AILLEURS — l'appel de capture, par exemple, dont l'échec
   *  appartient au serveur et pas à ce crochet. Un seul endroit affiche les refus liés aux
   *  pièces jointes ; deux emplacements de message, et l'utilisateur en manque un. */
  readonly setNote: (message: string | null) => void;
  readonly remove: (index: number) => void;
  readonly clear: () => void;
};

/** LE CŒUR, partagé par les DEUX boutons — le trombone et la capture de l'app.
 *
 *  UNE SEULE voie de réduction, et c'est délibéré : une capture prise par la plateforme doit
 *  obéir au MÊME plafond qu'un fichier choisi à la main, sinon le garde-fou de coût aurait un
 *  trou de la taille d'un bouton. Deux chemins auraient divergé.
 *
 *  `new Image()` plutôt que `createImageBitmap` : c'est le chemin qui existe partout depuis
 *  toujours, et je n'ai pas à parier sur la disponibilité d'une API plus récente.
 *
 *  `null` quand l'image est illisible. Ne lève jamais : un bouton qui lève laisse
 *  l'interface dans un état que personne n'a prévu. */
async function reduireDepuis(src: string): Promise<Attachment | null> {
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("image illisible"));
      i.src = src;
    });
    const { width, height } = fittedSize(img.naturalWidth, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    ctx.drawImage(img, 0, 0, width, height);
    const url = canvas.toDataURL("image/webp", QUALITE);
    // LE CONTRÔLE, pas l'hypothèse : on juge le type que le navigateur a rendu.
    if (!acceptedImageType(dataUrlType(url) ?? "")) return null;
    return { url, original: url, strokes: [], width, height };
  } catch {
    return null;
  }
}

/** Un fichier choisi par l'utilisateur. L'objet-URL est TOUJOURS révoqué : sans ça, l'image
 *  reste en mémoire tant que la page vit. */
async function reduireFichier(file: File): Promise<Attachment | null> {
  const src = URL.createObjectURL(file);
  try { return await reduireDepuis(src); }
  finally { URL.revokeObjectURL(src); }
}

/** Redessiner : l'originale, puis les traits par-dessus. `null` si l'image ne se relit pas.
 *
 *  C'est ce qui rend l'annotation SANS PERTE : on repart toujours de l'image d'origine, donc
 *  corriger dix fois ne ré-encode jamais du déjà ré-encodé. Le dessin lui-même vient de
 *  lib/paint_strokes — une seule définition, partagée avec l'éditeur. */
async function dessinerTraits(original: string, strokes: readonly Stroke[], size: { width: number; height: number }): Promise<string | null> {
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("image illisible"));
      i.src = original;
    });
    const canvas = document.createElement("canvas");
    canvas.width = size.width; canvas.height = size.height;
    const ctx = canvas.getContext("2d");
    if (ctx === null) return null;
    ctx.drawImage(img, 0, 0, size.width, size.height);
    paintStrokes(ctx, strokes);
    const url = canvas.toDataURL("image/webp", QUALITE);
    if (!acceptedImageType(dataUrlType(url) ?? "")) return null;
    return url;
  } catch {
    return null;
  }
}

export function useAttachments(): Attachments {
  const [items, setItems] = useState<readonly Attachment[]>([]);
  const [documents, setDocuments] = useState<readonly AttachedDocument[]>([]);
  const [note, setNote] = useState<string | null>(null);
  /** L'état COURANT, lisible depuis un rappel qui ne doit pas dépendre d'un rendu. Sans
   *  cette référence, `setStrokes` lirait la liste telle qu'elle était quand il a été
   *  créé — et annoterait la mauvaise image après un ajout. */
  const itemsRef = useRef(items);
  itemsRef.current = items;

  /** LA SEULE PORTE, pour une image comme pour un document.
   *
   *  UN SEUL TROMBONE : l'utilisateur choisit un fichier, la plateforme voit ce que c'est.
   *  Deux boutons auraient demandé de savoir d'avance dans quelle catégorie tombe son
   *  fichier — et un .csv n'est une « image » pour personne mais son type annoncé est
   *  souvent vide.
   *
   *  LE TRI N'INVENTE AUCUNE RÈGLE : `acceptedImageType` et `documentKind` sont les deux
   *  juges, chacun éprouvé chez lui. Ce qu'aucun ne reconnaît est refusé en le disant. */
  const add = useCallback(async (files: ArrayLike<File>) => {
    setNote(null);
    const images: Attachment[] = [];
    const docs: AttachedDocument[] = [];
    for (const f of Array.from(files)) {
      if (acceptedImageType(f.type)) {
        const a = await reduireFichier(f);
        if (a === null) { setNote(attachRefusal("unreadable")); continue; }
        images.push(a);
        continue;
      }
      if (documentKind(f.type ?? "", f.name ?? "") === null) { setNote(attachRefusal("bad-type")); continue; }
      // L'EXTRACTION EST ICI, dans le navigateur : ce qui partira est du texte, jamais le
      // fichier. Un PDF scanné rend `no-text`, dont le refus renvoie vers la photo.
      const lu = await readDocumentText(f);
      if (!lu.ok) { setNote(documentRefusal(lu.reason)); continue; }
      docs.push({ name: f.name, text: lu.text });
    }
    if (images.length > 0) {
      setItems((avant) => {
        // LE PLAFOND EST TENU SUR L'ÉTAT COURANT, pas sur ce lot : deux fois « une image »
        // doit buter comme « deux images d'un coup ».
        if (avant.length + images.length > ATTACH_MAX_IMAGES) {
          setNote(attachRefusal("too-many"));
          return avant;
        }
        return [...avant, ...images];
      });
    }
    if (docs.length > 0) {
      setDocuments((avant) => {
        if (avant.length + docs.length > DOC_MAX_FILES) {
          setNote(documentRefusal("too-many"));
          return avant;
        }
        const apres = [...avant, ...docs];
        // ON AVERTIT, ON NE REFUSE PAS : le serveur coupe au budget et le dit au modèle.
        // `null` quand on est dans le budget, donc rien ne s'affiche.
        const trop = documentBudgetNote(apres.reduce((n, d) => n + d.text.length, 0));
        if (trop !== null) setNote(trop);
        return apres;
      });
    }
  }, []);

  const addDataUrl = useCallback(async (url: string) => {
    setNote(null);
    const a = await reduireDepuis(url);
    if (a === null) { setNote(attachRefusal("unreadable")); return; }
    setItems((avant) => {
      if (avant.length + 1 > ATTACH_MAX_IMAGES) { setNote(attachRefusal("too-many")); return avant; }
      return [...avant, a];
    });
  }, []);

  const setStrokes = useCallback(async (index: number, strokes: readonly Stroke[]) => {
    setNote(null);
    // L'image d'origine, pas celle qui est affichée : c'est toute la raison de la garder.
    const cible = itemsRef.current[index];
    if (cible === undefined) return;
    const url = strokes.length === 0
      ? cible.original                                     // plus un trait ⇒ l'originale, telle quelle
      : await dessinerTraits(cible.original, strokes, { width: cible.width, height: cible.height });
    if (url === null) { setNote(attachRefusal("unreadable")); return; }
    setItems((avant) => avant.map((a, i) => (i === index ? { ...a, url, strokes } : a)));
  }, []);

  const remove = useCallback((index: number) => {
    setNote(null);
    setItems((avant) => avant.filter((_, i) => i !== index));
  }, []);

  const removeDocument = useCallback((index: number) => {
    setNote(null);
    setDocuments((avant) => avant.filter((_, i) => i !== index));
  }, []);

  const clear = useCallback(() => { setNote(null); setItems([]); setDocuments([]); }, []);

  return {
    items,
    documents,
    removeDocument,
    note,
    add,
    addDataUrl,
    setStrokes,
    setNote,
    remove,
    clear,
  };
}
